import { ref, computed, watch, type Ref } from "vue";
import { isEqual, cloneDeep } from "lodash";

/**
 * Determines if a value (primitive, array, or complex object) is "filled"
 * based on common-sense rules:
 *
 * - string  → non-empty (after trim)
 * - number  → valid (not NaN)
 * - boolean → always considered filled
 * - array   → not empty AND contains at least one meaningful element
 * - object  → not empty AND contains at least one meaningful property
 * - null/undefined → not filled
 */
function hasMeaningfulValue(value: unknown): boolean {
  switch (true) {
    // null or undefined → not filled
    case value == null:
      return false;

    // string → not empty
    case typeof value === "string":
      return value.trim().length > 0;

    // number → must be valid
    case typeof value === "number":
      return !isNaN(value);

    // boolean → always filled (even false has meaning)
    case typeof value === "boolean":
      return true;

    // array → must not be empty and at least one element filled
    case Array.isArray(value):
      return (value as unknown[]).length > 0 && (value as unknown[]).some((v) => hasMeaningfulValue(v));

    // object → must not be empty and at least one property filled
    case typeof value === "object": {
      const entries = Object.values(value as Record<string, unknown>);
      return entries.length > 0 && entries.some((v) => hasMeaningfulValue(v));
    }
    // fallback: anything else → not filled
    default:
      return false;
  }
}

/**
 * Creates a mutable model from a reactive initial value (primitive or complex),
 * with change detection against the original value.
 *
 * @param initialValueRef - A ref holding the initial/original value
 * @param options - Optional configuration
 * @returns An object with:
 *   - `model`: A mutable ref that can be edited
 *   - `hasChanges`: A computed boolean, true if `model` differs from `initialValueRef` (deep compare)
 *   - `isFilled`: A computed boolean, true if `model` has a meaningful value
 *   - `reset`: A function to reset `model` back to the initial value
 */
export function useComplexModelModelWithChanges<T>(
  initialValueRef: Ref<T>,
  options?: { normalizer?: (value: T) => T }
) {
  // Default normalizer that returns value unchanged
  const { normalizer = (value: T) => value } = options || {};

  // Local model: deep cloned to avoid shared references
  const model = ref<T>(cloneDeep(initialValueRef.value)) as Ref<T>;

  // Deep compare to detect changes
  const hasChanges = computed(() => {
    const initial = initialValueRef.value;
    const current = model.value;

    // Apply normalizer (defaults to identity function)
    const normalizedInitial = normalizer(initial);
    const normalizedCurrent = normalizer(current);

    return !isEqual(normalizedInitial, normalizedCurrent);
  });

  // Check if the model value is meaningful
  const isFilled = computed(() => {
    return hasMeaningfulValue(model.value);
  });

  // Keep model in sync with initialValueRef,
  // but only if the user hasn't modified `model`
  watch(initialValueRef, (newValue, oldValue) => {
    // Apply normalizer (defaults to identity function) for comparison
    const normalizedModel = normalizer(model.value);
    const normalizedOld = normalizer(oldValue);
    const normalizedNew = normalizer(newValue);

    const matchesOld = isEqual(normalizedModel, normalizedOld);
    const matchesNew = isEqual(normalizedModel, normalizedNew);

    if (matchesOld || matchesNew) {
      model.value = cloneDeep(newValue);
    }
  });

  // Reset model back to the current initial value
  function reset() {
    model.value = cloneDeep(initialValueRef.value);
  }

  return {
    model,
    hasChanges,
    isFilled,
    reset,
  };
}
